// FR-077 — Gift funnel analytics event definitions.
//
// Routes through Statsig (the existing analytics pipeline) via `trackEvent`,
// NOT through the backend. The backend `gift_share_events` table and
// `POST /v1/gifts/events` endpoint were removed; all events flow through
// the typed `onEvent` registry in `frontend/src/events/index.ts`.
//
// All wire names are prefixed `gift_` to namespace them away from existing
// non-gift events such as `claim_initiated` (trade claim flow) and
// `share_clicked` (market share button).

import type { EventDefinition } from './navigation';

export type GiftShareChannel =
  | 'x'
  | 'copy_link'
  | 'telegram'
  | 'whatsapp'
  | 'native'
  | 'discord'
  | 'imessage';

export type GiftModalOpenedProperties = Record<string, never>;
export type GiftModalDismissedProperties = Record<string, never>;
export type GiftSendInitiatedProperties = Record<string, never>;
export type GiftSendCompletedProperties = {
  depositPubkey: string;
};
export type GiftShareClickedXProperties = {
  depositPubkey?: string;
  channel: 'x';
};
export type GiftShareClickedCopyLinkProperties = {
  depositPubkey?: string;
  channel: 'copy_link';
};
// Unified share event used by <ShareGiftCta> on every surface that lets a
// sender share their gift permalink (success modal, sent-row, etc).
// Keeps the legacy `_x` / `_copy_link` events for back-compat with the
// claim-success-modal's "Thank on X" CTA (different intent — replying to
// sender, not sharing the permalink).
export type GiftShareClickedProperties = {
  depositPubkey?: string;
  channel: GiftShareChannel;
};
export type GiftPermalinkViewedProperties = {
  depositPubkey?: string;
};
export type GiftClaimInitiatedProperties = Record<string, never>;
export type GiftClaimCompletedProperties = Record<string, never>;
export type GiftWithdrawInitiatedProperties = {
  depositPubkey: string;
};
export type GiftWithdrawCompletedProperties = {
  depositPubkey: string;
};
export type GiftOnboardingCompletedViaClaimProperties = Record<string, never>;

export type GiftAssetType = 'usdc' | 'outcome_token';

export type GiftAssetSelectedProperties = {
  asset: GiftAssetType;
};

export type GiftOutcomeTokenPickedProperties = {
  mint: string;
  marketPubkey: string;
};

export const GIFT_MODAL_OPENED: EventDefinition<GiftModalOpenedProperties> = {
  name: 'gift_modal_opened',
};

export const GIFT_MODAL_DISMISSED: EventDefinition<GiftModalDismissedProperties> = {
  name: 'gift_modal_dismissed',
};

export const GIFT_SEND_INITIATED: EventDefinition<GiftSendInitiatedProperties> = {
  name: 'gift_send_initiated',
};

export const GIFT_SEND_COMPLETED: EventDefinition<GiftSendCompletedProperties> = {
  name: 'gift_send_completed',
};

export const GIFT_SHARE_CLICKED_X: EventDefinition<GiftShareClickedXProperties> = {
  name: 'gift_share_clicked_x',
};

export const GIFT_SHARE_CLICKED_COPY_LINK: EventDefinition<GiftShareClickedCopyLinkProperties> = {
  name: 'gift_share_clicked_copy_link',
};

export const GIFT_SHARE_CLICKED: EventDefinition<GiftShareClickedProperties> = {
  name: 'gift_share_clicked',
};

export const GIFT_PERMALINK_VIEWED: EventDefinition<GiftPermalinkViewedProperties> = {
  name: 'gift_permalink_viewed',
};

export const GIFT_CLAIM_INITIATED: EventDefinition<GiftClaimInitiatedProperties> = {
  name: 'gift_claim_initiated',
};

export const GIFT_CLAIM_COMPLETED: EventDefinition<GiftClaimCompletedProperties> = {
  name: 'gift_claim_completed',
};

export const GIFT_WITHDRAW_INITIATED: EventDefinition<GiftWithdrawInitiatedProperties> = {
  name: 'gift_withdraw_initiated',
};

export const GIFT_WITHDRAW_COMPLETED: EventDefinition<GiftWithdrawCompletedProperties> = {
  name: 'gift_withdraw_completed',
};

export const GIFT_ONBOARDING_COMPLETED_VIA_CLAIM: EventDefinition<GiftOnboardingCompletedViaClaimProperties> =
  {
    name: 'gift_onboarding_completed_via_claim',
  };

export const GIFT_ASSET_SELECTED: EventDefinition<GiftAssetSelectedProperties> = {
  name: 'gift_asset_selected',
};

export const GIFT_OUTCOME_TOKEN_PICKED: EventDefinition<GiftOutcomeTokenPickedProperties> = {
  name: 'gift_outcome_token_picked',
};
